'use client';

import { cn } from '@zealot/ui/lib/utils';
import { CloudUpload, Loader2, X } from 'lucide-react';
import * as React from 'react';

import { Button, buttonVariants } from './button';
import type { FileUploadFile, FileValidationError } from './file-upload';
import { FileUpload, FileUploadDropzone, FileUploadError, FileUploadTrigger } from './file-upload';

const WILDCARD_LABELS: Record<string, string> = {
  'image/*': 'PNG, JPG, GIF',
  'video/*': 'MP4, MOV, WebM',
  'audio/*': 'MP3, WAV, OGG',
  'application/pdf': 'PDF',
};

const deriveHint = (accept?: string, maxSize?: number): string | undefined => {
  const parts: string[] = [];

  if (accept) {
    const known = WILDCARD_LABELS[accept];
    if (known) {
      parts.push(known);
    } else {
      const labels = accept
        .split(',')
        .map((t) => {
          const trimmed = t.trim();
          if (trimmed.startsWith('.')) return trimmed.slice(1).toUpperCase();
          const sub = trimmed.split('/')[1];
          if (sub && sub !== '*') return sub.toUpperCase();
          return null;
        })
        .filter(Boolean);
      const unique = [...new Set(labels)];
      if (unique.length) parts.push(unique.join(', '));
    }
  }

  if (maxSize) {
    const mb = maxSize / 1_048_576;
    const sizeStr = mb >= 1 ? `${Math.round(mb)} MB` : `${Math.round(maxSize / 1024)} KB`;
    parts.push(`up to ${sizeStr}`);
  }

  return parts.length ? parts.join(' ') : undefined;
};

// gloss
type SimpleFileUploadProps = {
  files: FileUploadFile[];
  onFilesChange: (files: FileUploadFile[]) => void;
  onValidationError?: (errors: FileValidationError[]) => void;
  accept?: string;
  maxSize?: number;
  disabled?: boolean;
  isUploading?: boolean;

  alt: string;

  dropzoneVariant?: 'default' | 'solid' | 'ghost';
  dropzoneSize?: 'sm' | 'default' | 'lg';
  dropzoneClassName?: string;

  icon?: React.ReactNode;
  label?: string;
  hint?: string;

  changeLabel?: string;
  removeLabel?: string;

  previewSize?: 'sm' | 'default' | 'lg';

  error?: string;

  className?: string;

  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
};

const PREVIEW_SIZE_MAP = {
  sm: 'size-20',
  default: 'size-32',
  lg: 'size-40',
} as const;

const SimpleFileUpload = ({
  files,
  onFilesChange,
  onValidationError,
  accept,
  maxSize,
  disabled,
  isUploading,
  alt,
  dropzoneVariant,
  dropzoneSize,
  dropzoneClassName,
  icon,
  label = 'Upload Image',
  hint,
  changeLabel = 'Change Image',
  removeLabel = 'Remove',
  previewSize = 'default',
  error,
  className,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: SimpleFileUploadProps) => {
  const resolvedHint = hint !== undefined ? hint : deriveHint(accept, maxSize);
  const hasFile = files.length > 0;
  const currentFile = files[0];
  const isInteractionDisabled = disabled || isUploading;

  React.useEffect(() => {
    const url = currentFile?.previewUrl;
    return () => {
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [currentFile?.previewUrl]);

  const handleRemove = React.useCallback(() => {
    onFilesChange([]);
  }, [onFilesChange]);

  return (
    <FileUpload
      files={files}
      onFilesChange={onFilesChange}
      onValidationError={onValidationError}
      accept={accept}
      maxSize={maxSize}
      disabled={disabled}
      className={className}
      id={id}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid || undefined}
    >
      <FileUploadDropzone variant={dropzoneVariant} size={dropzoneSize} className={cn(dropzoneClassName)}>
        {!hasFile ? (
          isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <FileUploadTrigger>
              {icon ?? <CloudUpload className="size-8 text-muted-foreground" />}
              <span className="font-medium">{label}</span>
              {resolvedHint && <span className="text-muted-foreground">{resolvedHint}</span>}
            </FileUploadTrigger>
          )
        ) : (
          <div className="flex flex-col items-center gap-3">
            {}
            <div
              className={cn(
                'group relative overflow-hidden rounded-lg border border-border bg-muted transition-shadow hover:ring-2 hover:ring-primary/50',
                PREVIEW_SIZE_MAP[previewSize],
              )}
            >
              {currentFile?.previewUrl && (
                <img src={currentFile.previewUrl} alt={alt} className="size-full object-cover" />
              )}
              {}
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
                  <Loader2 className="size-6 animate-spin text-white" />
                </div>
              )}
              {}
              <Button
                variant="destructive"
                size="icon-xs"
                disabled={isInteractionDisabled}
                onClick={handleRemove}
                aria-label={`${removeLabel} ${currentFile?.file.name ?? ''}`.trim()}
                className="absolute top-1 right-1 rounded-full shadow-sm"
              >
                <X />
              </Button>
            </div>
            {}
            <FileUploadTrigger
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
              disabled={isInteractionDisabled}
            >
              {changeLabel}
            </FileUploadTrigger>
          </div>
        )}
      </FileUploadDropzone>
      <FileUploadError>{error}</FileUploadError>
    </FileUpload>
  );
};

export type { SimpleFileUploadProps };
export { SimpleFileUpload };
